import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

// Add auth token to requests
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface NewsletterSubscriber {
  id: string;
  email: string;
  creatorProfileId: string;
  tags: string[];
  status: 'SUBSCRIBED' | 'UNSUBSCRIBED' | 'BOUNCED';
  subscribedAt: Date;
  unsubscribedAt?: Date;
  source?: string;
}

export interface NewsletterStats {
  subscribed: number;
  unsubscribed: number;
  bounced: number;
}

export interface NewsletterFilters {
  status?: 'SUBSCRIBED' | 'UNSUBSCRIBED' | 'BOUNCED';
  tags?: string[];
  page?: number;
  limit?: number;
}

class NewsletterService {
  /**
   * Subscribe to a creator's newsletter
   */
  async subscribe(creatorProfileId: string, email: string, tags?: string[]): Promise<NewsletterSubscriber> {
    const response = await axios.post(
      `${API_URL}/api/newsletters/subscribe`,
      { creatorProfileId, email, tags },
      { headers: getAuthHeaders() }
    );
    return response.data.subscriber;
  }

  /**
   * Unsubscribe from a newsletter
   */
  async unsubscribe(email: string, creatorProfileId: string): Promise<void> {
    await axios.post(
      `${API_URL}/api/newsletters/unsubscribe`,
      { email, creatorProfileId },
      { headers: getAuthHeaders() }
    );
  }

  /**
   * Get newsletter subscribers (creator/admin only)
   */
  async getSubscribers(
    creatorProfileId: string,
    filters?: NewsletterFilters
  ): Promise<{ subscribers: NewsletterSubscriber[]; total: number; page: number; limit: number }> {
    const response = await axios.get(`${API_URL}/api/newsletters/${creatorProfileId}/subscribers`, {
      params: filters,
      headers: getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * Update subscriber tags (creator/admin only)
   */
  async updateSubscriberTags(subscriberId: string, tags: string[]): Promise<NewsletterSubscriber> {
    const response = await axios.put(
      `${API_URL}/api/newsletters/subscribers/${subscriberId}/tags`,
      { tags },
      { headers: getAuthHeaders() }
    );
    return response.data.subscriber;
  }

  /**
   * Get newsletter statistics (creator/admin only)
   */
  async getStats(creatorProfileId: string): Promise<NewsletterStats> {
    const response = await axios.get(`${API_URL}/api/newsletters/${creatorProfileId}/stats`, {
      headers: getAuthHeaders(),
    });
    return response.data.stats;
  }

  /**
   * Export subscribers as CSV (creator/admin only)
   */
  async exportSubscribers(creatorProfileId: string, filters?: NewsletterFilters): Promise<Blob> {
    const response = await axios.get(`${API_URL}/api/newsletters/${creatorProfileId}/export`, {
      params: filters,
      headers: getAuthHeaders(),
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Sync subscribers to external service (creator/admin only)
   */
  async syncToExternalService(
    creatorProfileId: string,
    service: 'mailchimp' | 'sendgrid' | 'convertkit'
  ): Promise<{ success: boolean; synced: number }> {
    const response = await axios.post(
      `${API_URL}/api/newsletters/${creatorProfileId}/sync`,
      { service },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Get all newsletters for a creator
   */
  async getNewsletters(creatorProfileId: string): Promise<NewsletterSubscriber[]> {
    const response = await axios.get(`${API_URL}/api/newsletters/${creatorProfileId}`, {
      headers: getAuthHeaders(),
    });
    return response.data.subscribers;
  }
}

export default new NewsletterService();
